// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	DropdownItem,
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputDropdownWithoutInputTextBoxProps,
	InputFormProps,
} from '@app/props';

interface UseInputDropdownParameters  {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	value: string;
	dropdownItems: DropdownItem[];
	defaultDropdownItem: DropdownItem;
	showValidation?: boolean;
}

type UseInputDropdownResults = UseInputFormResult<string, InputDropdownWithoutInputTextBoxProps>

const useInputDropdown = (params: UseInputDropdownParameters): UseInputDropdownResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyInputDropdownProps = (): InputDropdownWithoutInputTextBoxProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the dropdown props.
		return {
			...formProps,
			dropdownItems: params.dropdownItems,
			defaultDropdownItem: params.defaultDropdownItem,
			onSelect: setValue,
			value: other.value,
		}
	};

	// #endregion exported methods


	// #region private methods

	const setValue = (dropdownItem: DropdownItem): void => {
		other.setInput(dropdownItem.value);
	}

	/**
	 * Checks to see if the selected data is a valid option.
	 * @param selectedData
	 */
	const isSelectedValueValid = (selectedValue: string): boolean => {
		// Make sure that any selected possible data is a valid option.
		let matchingDropdownItem: DropdownItem | undefined = params.dropdownItems.find((dropdownItem: DropdownItem) => dropdownItem.value === selectedValue);
		return matchingDropdownItem !== undefined;
	}

	/**
	 * Validates the selected dropdown value.
	 * @param value
	 */
	const validateDropdown = (selectedValue: string, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& (String.isNullOrWhiteSpace(selectedValue) || selectedValue === '0')) {
			// Make sure there is a selected value.
			response = new ValidatorResponse(false, 'An answer is required.');
		}
		else if (!String.isNullOrWhiteSpace(selectedValue)
			&& selectedValue !== '0'
			&& !isSelectedValueValid(selectedValue)) {
			// Make sure the selected value is valid.
			response = new ValidatorResponse(false, 'Answer is invalid.');
		}

		return response;
	}

	/**
	 * Sets up the intial value of the input.
	 * @param initialValue
	 */
	const setupInitialValue = (initialValue: string): string => {
		let updatedInitialValue: string = initialValue;
		if (updatedInitialValue !== undefined
			&& !isSelectedValueValid(updatedInitialValue)) {
			updatedInitialValue = params.defaultDropdownItem.value;
		}

		return updatedInitialValue;
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'address' input.
	const { applyProps, ...other } = useInputForm<string>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: setupInitialValue(params.value),
		showValidation: params.showValidation,
		validators: [validateDropdown],
	});

	// #endregion variables


	return {
		applyProps: applyInputDropdownProps,
		...other,
	};
}

// Export parameters.
export {
	UseInputDropdownParameters,
}

// Export results.
export {
	UseInputDropdownResults,
}

export default useInputDropdown;